import { NavLink } from 'react-router-dom';
import { BookOpen, MessageCircle, Library, Heart, Award } from 'lucide-react';
import { useDM } from '@/context/DMContext';
import type { TabId } from '@/types';

/**
 * Tab configuration with routes.
 * Each tab maps to a top-level route in the app.
 */
const tabs: { id: TabId; icon: typeof BookOpen; label: string; to: string }[] = [
  { id: 'study', icon: BookOpen, label: 'Study', to: '/study' },
  { id: 'chat', icon: MessageCircle, label: 'Shmooze', to: '/chat' },
  { id: 'library', icon: Library, label: 'Library', to: '/library' },
  { id: 'prayers', icon: Heart, label: 'Prayers', to: '/prayers' },
  { id: 'profile', icon: Award, label: 'Journey', to: '/profile' },
];

// Navigation gradient - 50% intensity, fading downward into white
// Mirrors the header gradient but in reverse direction
const NAV_GRADIENT = 'linear-gradient(to bottom, #fdf8fd 0%, #fefcfe 40%, #ffffff 100%)';

// Soft shadow above the nav bar
const SHADOW_GRADIENT =
  'linear-gradient(to top, rgba(240, 210, 240, 0.25) 0%, rgba(240, 210, 240, 0.08) 50%, transparent 100%)';

interface BottomNavigationProps {
  /**
   * When true, the nav slides off-screen via CSS transform.
   * The element stays in the DOM for accessibility and to avoid layout reflow.
   */
  hidden?: boolean;
}

/**
 * BottomNavigation - Self-contained bottom navigation bar.
 *
 * Architecture:
 * - Nav content region: exactly `--nav-height` (60px) tall
 * - Safe area fill region: extends the background through the device safe area
 * - Visibility: controlled via CSS `transform: translateY` (no conditional rendering)
 * - Shadow: gradient-based soft shadow rendered above the nav
 *
 * This component owns its own safe-area handling so AppShell doesn't need
 * separate fixed-position cover divs that cause Safari stacking issues.
 */
export function BottomNavigation({ hidden = false }: BottomNavigationProps) {
  const { totalUnreadCount } = useDM();

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      data-bottom-nav
      className="relative will-change-transform"
      style={{
        transform: hidden ? 'translateY(100%)' : 'translateY(0)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        // Prevent Safari from showing content below during transform
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* Smooth anti-aliased shadow drop above - gradient-based for better blending */}
      <div
        className="absolute left-0 right-0 -top-3 h-3 pointer-events-none"
        style={{ background: SHADOW_GRADIENT }}
        aria-hidden="true"
      />

      {/* Nav content region - exactly --nav-height tall, never clipped */}
      <div style={{ height: 'var(--nav-height)', background: NAV_GRADIENT }}>
        <div className="max-w-md mx-auto h-full flex justify-between px-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const showBadge = tab.id === 'chat' && totalUnreadCount > 0;

            return (
              <NavLink
                key={tab.id}
                to={tab.to}
                className={({ isActive }) => `
                  flex flex-col items-center justify-center py-3 px-2 flex-1 transition-all duration-200 relative
                  ${isActive ? 'text-[#8e4585]' : 'text-slate-400 hover:text-slate-600'}
                  active:scale-95
                `}
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`relative transition-transform duration-200 ${isActive ? 'scale-110' : ''}`}
                    >
                      <Icon size={22} strokeWidth={isActive ? 2.5 : 2} />

                      {/* Unread badge for Shmooze tab */}
                      {showBadge && (
                        <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-[#8e4585] text-white text-[9px] font-bold rounded-full">
                          {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] font-medium mt-1 font-['Merriweather']">
                      {tab.label}
                    </span>
                    {/* Active indicator */}
                    <span
                      className={`absolute -bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#8e4585] rounded-full transition-all duration-300 ${isActive ? 'w-8 opacity-100' : 'w-0 opacity-0'}`}
                    />
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </div>

      {/*
       * Safe area fill region.
       * Extends the white background through the device safe area (e.g., iPhone home indicator).
       * This is ADDITIVE to --nav-height, not subtracted from it, so the nav content
       * always gets its full 60px regardless of safe area size.
       */}
      <div
        aria-hidden="true"
        style={{
          height: 'env(safe-area-inset-bottom, 0px)',
          background: '#ffffff',
        }}
      />
    </nav>
  );
}

export default BottomNavigation;
